import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
// eslint-disable-next-line no-restricted-imports -- legacy junk-drawer file; rename in plans/cached-singing-fiddle.md
import { requireWorkspaceRole } from "./helpers";
import { makeWorkspaceId, makeUserId } from "../types/branded-ids";

export const coworkersMutators = {
  create: defineMutator(
    z.object({
      id: z.string(),
      workspace_id: z.string(),
      created_by: z.string(),
      role_title: z.string(),
      expertise: z.string(),
      personality: z.string(),
      boundaries: z.string().optional(),
      custom_instructions: z.string().optional(),
      model_provider: z.string().optional(),
      model_id: z.string().optional(),
      allowed_skill_slugs: z.array(z.string()).optional(),
      enabled: z.boolean().optional(),
      respond_to_mentions: z.boolean().optional(),
      respond_to_dms: z.boolean().optional(),
      created_at: z.number(),
      updated_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.created_by !== ctx.sub) {
        throw new Error("Unauthorized: created_by mismatch");
      }
      if (tx.location === "server") {
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(args.workspace_id),
          makeUserId(ctx.sub),
        );
      }
      await tx.mutate.coworkers.insert({
        ...args,
        boundaries: args.boundaries ?? "",
        custom_instructions: args.custom_instructions ?? "",
        model_provider: args.model_provider ?? "anthropic-managed",
        model_id: args.model_id ?? "claude-haiku-4-5-20251001",
        allowed_skill_slugs: args.allowed_skill_slugs ?? [],
        allowed_tool_scope: "all",
        enabled: args.enabled ?? true,
        respond_to_mentions: args.respond_to_mentions ?? true,
        respond_to_dms: args.respond_to_dms ?? true,
        mode: "managed",
      });
    },
  ),

  update: defineMutator(
    z.object({
      id: z.string(),
      role_title: z.string().optional(),
      expertise: z.string().optional(),
      personality: z.string().optional(),
      boundaries: z.string().optional(),
      custom_instructions: z.string().optional(),
      model_provider: z.string().optional(),
      model_id: z.string().optional(),
      allowed_skill_slugs: z.array(z.string()).optional(),
      enabled: z.boolean().optional(),
      respond_to_mentions: z.boolean().optional(),
      respond_to_dms: z.boolean().optional(),
      updated_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.coworkers.where("id", args.id).one());
        if (!entity) return;
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(entity.workspace_id),
          makeUserId(ctx.sub),
        );
      }
      await tx.mutate.coworkers.update(args);
    },
  ),

  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.coworkers.where("id", args.id).one());
        if (!entity) return;
        await requireWorkspaceRole(
          tx,
          makeWorkspaceId(entity.workspace_id),
          makeUserId(ctx.sub),
        );
      }
      await tx.mutate.coworkers.delete(args);
    },
  ),
};
